import React, { useState, useEffect, useMemo } from 'react';
import { Table, Badge, Card } from 'react-bootstrap';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { useSelector } from 'react-redux';
import { selectProjectResults } from '../data-access/project.reducer';
import { pickGovernanceEntry } from '../../shared/governanceResultsAccess';

interface OntologyTabProps {
  projectId: string;
  userId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OntologyTab = (_props: OntologyTabProps) => {
  const projectResults = useSelector(selectProjectResults);
  const [data, setData] = useState<any | null>(null);

  const ontologyData = useMemo(() => pickGovernanceEntry(projectResults, ['ontology']), [projectResults]);

  useEffect(() => {
    setData(ontologyData ?? null);
  }, [ontologyData]);

  const getRiskBadge = (risk: string) => {
    switch (risk?.toUpperCase()) {
      case 'LOW':
        return <Badge bg="success">LOW</Badge>;
      case 'MEDIUM':
        return (
          <Badge bg="warning" text="dark">
            MEDIUM
          </Badge>
        );
      case 'HIGH':
        return <Badge bg="danger">HIGH</Badge>;
      case 'CRITICAL':
        return <Badge bg="dark">CRITICAL</Badge>;
      default:
        return <Badge bg="secondary">{risk}</Badge>;
    }
  };

  const getOverallStatusBadge = (status: string) => {
    return status === 'AT_RISK' ? <Badge bg="danger">AT RISK</Badge> : <Badge bg="success">PASS</Badge>;
  };

  return (
    <div className="mb-4">
      <PhoenixDocCard className="mb-4">
        <PhoenixDocCard.Header title="Ontology Analysis (MLP v2)">
          <p className="text-muted mb-0">
            MLP v2 Ontology based on project analysis results (Bias, Fairness, Explainability, etc.).
          </p>
        </PhoenixDocCard.Header>
        <PhoenixDocCard.Body>
          {data && (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-4 mb-4 p-3 bg-light rounded border">
                <div>
                  <h5 className="mb-1">Overall Risk</h5>
                  {getOverallStatusBadge(data.overall_risk)}
                </div>
                <div>
                  <h5 className="mb-1">Ontology Version</h5>
                  <span className="fw-bold text-primary">{data.ontology_version || 'N/A'}</span>
                </div>
              </div>

              <h5 className="mb-3">Domain Analysis</h5>
              <div className="table-responsive">
                <Table bordered hover className="align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: '20%' }}>Risk Domain</th>
                      <th style={{ width: '10%' }}>Risk Level</th>
                      <th style={{ width: '30%' }}>Interpretation</th>
                      <th style={{ width: '20%' }}>References</th>
                      <th style={{ width: '20%' }}>Provenance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.domains || {}).map(([key, domain]: [string, any]) => (
                      <tr key={key}>
                        <td className="fw-semibold">{domain.risk_domain || key}</td>
                        <td>{getRiskBadge(domain.risk_level)}</td>
                        <td>
                          {domain.interpretation ? (
                            Array.isArray(domain.interpretation) ? (
                              <ul className="mb-0 ps-3">
                                {domain.interpretation.map((item: string, idx: number) => (
                                  <li key={idx} className="small text-muted">
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="small text-muted">{domain.interpretation}</div>
                            )
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                        <td>
                          {domain.references &&
                            Object.entries(domain.references).map(([refKey, refVal]: [string, any]) => (
                              <div key={refKey} className="mb-1 small">
                                <span className="fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
                                  {refKey}:
                                </span>{' '}
                                <span className="text-muted">{refVal}</span>
                              </div>
                            ))}
                        </td>
                        <td>
                          {domain.provenance ? (
                            Array.isArray(domain.provenance) ? (
                              domain.provenance.map((prov: string, idx: number) => (
                                <div
                                  key={idx}
                                  className="small text-muted font-monospace mb-1"
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {prov}
                                </div>
                              ))
                            ) : (
                              <div className="small text-muted font-monospace mb-1" style={{ fontSize: '0.75rem' }}>
                                {domain.provenance}
                              </div>
                            )
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {data.reasoning_layer && (
                <div className="mt-4">
                  <h5 className="mb-3">Reasoning Layer</h5>
                  <Card className="bg-light border-0">
                    <Card.Body>
                      {Array.isArray(data.reasoning_layer) ? (
                        <ul className="mb-0">
                          {data.reasoning_layer.map((reason: string, idx: number) => (
                            <li key={idx} className="mb-1">
                              {reason}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="mb-0">{data.reasoning_layer}</div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              )}
            </div>
          )}

          {!data && (
            <div className="text-center py-5 text-muted">
              <h5>No Analysis Data</h5>
              <p>No Ontology analysis data available for this project.</p>
            </div>
          )}
        </PhoenixDocCard.Body>
      </PhoenixDocCard>
    </div>
  );
};

export default OntologyTab;
